import React from 'react';
import { bool, func, number, object, shape, string } from 'prop-types';
import { Avatar, Tooltip } from '@fiverr-private/fit';
import { getContext } from '@fiverr-private/fiverr_context';
import { I18n } from '@fiverr-private/i18n-react';
import { SellerLevel } from '@fiverr-private/profile_image';
import { Rating } from '@fiverr-private/orca';
import { BQ_CLICKED_REVIEWS_RATINGS, BQ_CLICKED_SELLER_NAME_ANCHOR } from '../../../utils/bigQueryEvents';
import { MP_CLICKED_SELLER_NAME_ANCHOR } from '../../../utils/mixPanelEvents';
import { ABOUT_SELLER, REVIEWS } from '../../../utils/pageSections';
import { getSellerLanguageParams } from '../../../utils/sellerLanguage';
import RatingWrapper from '../../shared/RatingWrapper';
import SellerLanguage from '../../shared/SellerLanguage';
import Badges from '../Badges';
import LazyFlag from '../LazyFlag';

import classes from './index.module.scss';

const IMAGE_SIZE = 30;

const SellerProfile = ({
    overview,
    studioInfo,
    useStickyLayout,
    shouldRenderSellerLanguage
}, { biEvents, general, scrollToSection }) => {
    const { isMobile, locale, browserLanguage, countryCode } = getContext();
    const { seller, gig = {} } = overview;
    const { ordersInQueue, ratingsCount, rating } = gig;
    const hasOrdersInQueue = ordersInQueue > 0;
    const hasRating = ratingsCount > 0 && !!rating;
    const ratingTheme = isMobile ? Rating.THEMES.ONE_STAR : Rating.THEMES.FIVE_STARS;
    const { isStudio } = general;
    const { profilePhoto, isPro, achievement, name, username } = isStudio ? studioInfo : seller;
    const profile = {
        name: isStudio ? name : username,
        profilePhoto,
        isPro,
        achievement
    };

    const sellerLanguageParams = shouldRenderSellerLanguage ? getSellerLanguageParams({
        buyer: { locale, browserLanguage, countryCode },
        seller
    }) : null;

    const handleSellerNameClick = () => {
        if (!useStickyLayout) { return; }
        biEvents.sendMixPanelEvent({ eventName: MP_CLICKED_SELLER_NAME_ANCHOR });
        biEvents.sendBigQueryEvent({ eventName: BQ_CLICKED_SELLER_NAME_ANCHOR });
        scrollToSection({ sectionKey: ABOUT_SELLER });
    };

    const getCountryTooltip = (countryCode) => (
        <>
            <b><I18n k="gig_page_perseus.overview.seller_location"/>:</b>
            <span>&nbsp;<I18n k={`countries.${countryCode}`}/></span>
        </>
    );

    const onReviewsClick = () => {
        biEvents.sendBigQueryEvent({ eventName: BQ_CLICKED_REVIEWS_RATINGS });
        scrollToSection({ sectionKey: REVIEWS });
    };

    return (
        <div className={classes.overviewWrapper}>
            <Avatar containerClassName={classes.avatar}
                src={profile.profilePhoto}
                username={profile.name}
                size={IMAGE_SIZE}/>
            <div className={classes.sellerInfo}>
                <div className={classes.name} onClick={handleSellerNameClick}>
                    {profile.name}
                </div>
                { sellerLanguageParams?.sellerCountryCode &&
                    <Tooltip className={classes.country}
                        content={getCountryTooltip(sellerLanguageParams.sellerCountryCode)}>
                        <LazyFlag countryCode={sellerLanguageParams.sellerCountryCode} size={16}/>
                    </Tooltip>
                }
                <SellerLevel className={classes.sellerLevel} {...profile}/>
                { hasRating &&
                    <RatingWrapper className={classes.userInfoRating}
                        onClick={onReviewsClick}
                        rating={rating}
                        ratingsCount={ratingsCount}
                        theme={ratingTheme}/>
                }
                {hasOrdersInQueue &&
                <div className={classes.ordersInQueue}>
                    <I18n k="gig_page_perseus.overview.orders_in_queue_single_plural"
                        params={{ count: ordersInQueue }}/>
                </div>
                }
                <Badges/>
                {shouldRenderSellerLanguage &&
                <div className={classes.languages}>
                    <SellerLanguage sellerLanguages={sellerLanguageParams.sellerValidatedLanguages}
                        commonLanguage={sellerLanguageParams.commonLanguage}
                        withLevel={false}/>
                </div>
                }
            </div>
        </div>
    );
};

SellerProfile.propTypes = {
    seller: shape({
        id: number.isRequired,
        username: string,
        achievement: number,
        isPro: bool,
        hasProfilePhoto: bool,
        profilePhoto: string
    }),
    studioInfo: shape({
        id: number,
        name: string,
        slug: string,
        tagline: string,
        description: string,
        profilePhoto: string,
        reviewsCount: number,
        rating: number
    }),
    useStickyLayout: bool,
    shouldRenderSellerLanguage: bool
};

SellerProfile.contextTypes = {
    biEvents: object,
    general: object,
    scrollToSection: func
};

export default SellerProfile;
